/**
 * Ce qu'une suppression de compte engage, et comment on le confirme.
 *
 * POURQUOI CE FICHIER : le geste actuel est un `window.confirm` portant le
 * seul nom de la personne. Pour une action irréversible qui efface des
 * années de contenu et résilie des abonnements payants, c'est trop peu —
 * et rien n'annonce ce qui sera conservé.
 *
 * TROIS CHOSES DOIVENT ÊTRE DITES AVANT :
 *
 *   ce qui part      — le compte, les contenus, les fichiers
 *   ce qui reste     — les factures, que la loi impose de garder 7 ans
 *   ce qui s'arrête  — les abonnements en cours, résiliés chez Mollie
 *
 * Taire le deuxième point serait malhonnête : la personne croirait tout
 * effacé alors qu'une pièce comptable subsiste, rattachée à son numéro
 * client. Le RGPD autorise cette conservation (art. 17.3.b), à condition
 * de l'annoncer.
 */

/**
 * Vérifie que la confirmation saisie correspond bien au compte.
 *
 * L'ADRESSE E-MAIL PLUTÔT QU'UN MOT À RECOPIER : retaper « SUPPRIMER »
 * devient un réflexe qu'on exécute sans lire. Retaper sa propre adresse
 * oblige à se demander de quel compte il s'agit — ce qui est précisément
 * la question à se poser avant un geste irréversible.
 *
 * La comparaison ignore la casse et les espaces de bord : une majuscule
 * de clavier mobile ne doit pas faire échouer une intention claire.
 */
export function confirmationValide(saisie, emailDuCompte) {
    const a = String(saisie ?? '').trim().toLowerCase();
    const b = String(emailDuCompte ?? '').trim().toLowerCase();

    if (!a || !b) return false;
    return a === b;
}

/**
 * Ce que la suppression va faire, pour l'afficher avant de la lancer.
 *
 * Rend des CLÉS de traduction, jamais des phrases : l'écran est en huit
 * langues, et un texte en dur ici serait français pour un Grec.
 *
 * @param {object} contexte { aUnAbonnement, aDesFactures }
 */
export function consequencesSuppression(contexte) {
    const c = contexte || {};

    const effacé = [
        'profil',
        'contenus',
        'fichiers',
        'quartiers'
    ];

    const conservé = [];
    if (c.aDesFactures) {
        // Obligation comptable de sept ans en droit belge. Rattachées au
        // seul numéro client, elles ne désignent plus une personne
        // identifiable une fois le compte parti.
        conservé.push('factures');
    }

    const arrêté = [];
    if (c.aUnAbonnement) {
        arrêté.push('abonnements');
    }

    return { effacé, conservé, arrêté };
}

/**
 * Vrai si la suppression peut être lancée.
 *
 * DEUX CONDITIONS, et les deux comptent : la confirmation saisie doit
 * correspondre, ET la personne doit avoir reconnu avoir lu les
 * conséquences. Un seul verrou se franchit par habitude.
 */
export function peutSupprimer({ saisie, email, consequencesLues }) {
    if (!consequencesLues) return false;
    return confirmationValide(saisie, email);
}

/**
 * Traduit l'échec d'une suppression en clé de message.
 *
 * LE CAS 402 EST LE PLUS IMPORTANT : le serveur refuse de supprimer un
 * compte dont l'abonnement n'a pas pu être résilié chez Mollie. C'est
 * voulu — un compte effacé dont le prélèvement continue serait
 * irréparable, faute de savoir qui rembourser. Le message doit donc
 * inviter à réessayer, pas laisser croire à une panne définitive.
 */
export function motifEchec(statut) {
    const code = Number(statut) || 0;

    if (code === 402) return 'resiliationImpossible';
    if (code === 403) return 'pasVotreCompte';
    if (code === 401) return 'sessionExpiree';
    if (code >= 500) return 'erreurServeur';
    return 'erreurInconnue';
}
